// ReportTable.tsx
import {
  getAssessmentTypeLabel,
  IStyles,
  Loader,
  NoContentCard,
  pxToRem,
  pxTovW,
} from '@geneo2-web/shared-ui';
import {
  Box,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from '@mui/material';
import { SxProps } from '@mui/system';
import { useEffect, useMemo, useState } from 'react';
import {
  useAppSelector,
  useDeserifiedSelector,
} from '../../../reduxStore/reduxHooks';
import { AssessmentEnum } from '@protos/learning_management/lms.db_pb';
import { StudentReportCenterData } from '@protos/learning_management/lms.assessment.student.apis_pb';
import { visuallyHidden } from '@mui/utils';
const styles: IStyles = {
  root: {},
  tableContainer: {
    border: '1px solid #E8E8E8',
    borderRadius: { xs: pxToRem(10), md: pxTovW(10) },
    mt: { xs: pxToRem(25), md: pxTovW(25) },
    mb: { xs: pxToRem(20), md: pxTovW(50) },
  },
  headerRow: {
    backgroundColor: '#F3F9FD',
    padding: {
      xs: `${pxToRem(0)} ${pxToRem(10)}`,
      md: `${pxTovW(0)} ${pxTovW(20)}`,
    },
    boxSizing: 'border-box',
  },
  headerCell: {
    padding: {
      xs: `${pxToRem(15)} ${pxToRem(7)}`,
      md: `${pxTovW(20)} ${pxTovW(10)} ${pxTovW(20)} ${pxTovW(0)}`,
      lg: `${pxTovW(20)} ${pxTovW(0)} ${pxTovW(20)} ${pxTovW(0)}`,
    },
  },
  bodyCells: {
    padding: {
      xs: `${pxToRem(15)} ${pxToRem(7)}`,
      md: `${pxTovW(20)} ${pxTovW(0)} ${pxTovW(20)} ${pxTovW(0)}`,
    },
  },
};

interface Header {
  name: string;
  cellStyle?: SxProps;
  align?: 'left' | 'center' | 'right';
}

interface ReportTableProps {
  headers: Header[];
  searchText?: string;
  searchByAssessType?: AssessmentEnum;
  onDownloadClick?: (url: string) => void;
  cellSx?: SxProps;
  loading: 'loading' | 'error' | 'completed';
  icon?: string;
  generateReports?: (row: StudentReportCenterData) => Promise<void>;
}

export const ReportTable = ({
  headers,
  onDownloadClick,
  cellSx,
  searchText,
  loading,
  generateReports,
  searchByAssessType,
  icon,
}: ReportTableProps) => {
  const reportsData = useDeserifiedSelector(
    (state) => state.reports.reportsData
  );
  const [orderBy, setOrderBy] = useState<string>('');
  const [order, setOrder] = useState<'asc' | 'desc'>('asc');

  const handleSort = (field: string) => {
    if (orderBy === field) {
      setOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setOrderBy(field);
      setOrder('asc');
    }
  };

  const sortedRows = useMemo(() => {
    if (!reportsData?.studentReports) return [];

    const sorted = [...reportsData.studentReports];

    if (!orderBy) return sorted;

    sorted.sort((a, b) => {
      let aVal: string | number = '';
      let bVal: string | number = '';

      if (orderBy === 'completionDate') {
        const aSeconds = a.completionDate?.seconds;
        const bSeconds = b.completionDate?.seconds;

        aVal = aSeconds ? Number(aSeconds.toString().replace(/[^\d]/g, '')) : 0;
        bVal = bSeconds ? Number(bSeconds.toString().replace(/[^\d]/g, '')) : 0;

        // Debug check
        // console.log('Sort by date:', aVal, bVal);
      } else if (orderBy === 'assessmentName') {
        aVal = a.assessmentName?.toLowerCase?.() ?? '';
        bVal = b.assessmentName?.toLowerCase?.() ?? '';
      } else {
        const aField = a[orderBy as keyof typeof a];
        const bField = b[orderBy as keyof typeof b];

        aVal =
          typeof aField === 'object' && aField !== null && 'toString' in aField
            ? aField.toString().toLowerCase?.()
            : (aField ?? '').toString().toLowerCase?.();

        bVal =
          typeof bField === 'object' && bField !== null && 'toString' in bField
            ? bField.toString().toLowerCase?.()
            : (bField ?? '').toString().toLowerCase?.();
      }

      if (aVal < bVal) return order === 'asc' ? -1 : 1;
      if (aVal > bVal) return order === 'asc' ? 1 : -1;
      return 0;
    });

    return sorted;
  }, [reportsData?.studentReports, orderBy, order]);

  return (
    <TableContainer sx={styles.tableContainer}>
      {loading === 'error' ? (
        <NoContentCard variant="error" icon="error" text="Error Occurred" />
      ) : loading === 'loading' ? (
        <Loader />
      ) : sortedRows.length === 0 ? (
        <NoContentCard variant="info" icon="cards" text="No Data available" />
      ) : (
        <Table>
          <TableHead
            sx={{
              borderTop: '2px solid #007CDC',
              borderBottom: '2px solid #007CDC',
            }}
          >
            <TableRow sx={styles.headerRow}>
              {headers.map((header, idx) => {
                const isSortable = [
                  'SUBMISSION DATE',
                  'ASSESSMENT NAME',
                ].includes(header.name);
                const sortKey =
                  header.name === 'SUBMISSION DATE'
                    ? 'completionDate'
                    : header.name === 'ASSESSMENT NAME'
                    ? 'assessmentName'
                    : '';

                return (
                  <TableCell key={idx} align={header.align ?? 'center'}>
                    {isSortable ? (
                      <TableSortLabel
                        active={orderBy === sortKey}
                        direction={orderBy === sortKey ? order : 'asc'}
                        hideSortIcon={false}
                        onClick={() => handleSort(sortKey)}
                        sx={{
                          fontWeight: 'bold',
                          fontSize: pxToRem(14), // or use theme.typography.h3
                          color: '#000',

                          '& .MuiTableSortLabel-icon': {
                            opacity: 1,
                            color:
                              orderBy === sortKey
                                ? '#000 !important'
                                : '#8D8D8D !important',
                          },
                        }}
                      >
                        <Typography
                          variant="h3"
                          fontWeight="bold"
                          noWrap
                          textAlign="center"
                        >
                          {header.name}
                        </Typography>
                        <Box component="span" sx={visuallyHidden}>
                          {order === 'desc'
                            ? 'sorted descending'
                            : 'sorted ascending'}
                        </Box>
                      </TableSortLabel>
                    ) : (
                      <Typography
                        variant="h3"
                        fontWeight="bold"
                        noWrap
                        textAlign="center"
                      >
                        {header.name}
                      </Typography>
                    )}
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>

          <TableBody>
            {sortedRows.map((row, rowIndex) => (
              <TableRow
                key={rowIndex}
                sx={{
                  backgroundColor: rowIndex % 2 === 0 ? '#fff' : '#F3F9FD',
                }}
              >
                <TableCell sx={{ textAlign: 'center', ...cellSx }}>
                  <Typography variant="h3" noWrap>
                    {row.completionDate?.seconds
                      ? (() => {
                          const timestamp = Number(
                            row.completionDate.seconds
                              .toString()
                              .replace(/[^\d]/g, '')
                          );
                          const date = new Date(timestamp * 1000);
                          const day = String(date.getDate()).padStart(2, '0');
                          const month = String(date.getMonth() + 1).padStart(
                            2,
                            '0'
                          );
                          const year = date.getFullYear();
                          return `${day}-${month}-${year}`;
                        })()
                      : '-'}
                  </Typography>
                </TableCell>

                <TableCell
                  sx={{
                    textAlign: 'center',
                    maxWidth: pxToRem(100),
                    ...cellSx,
                  }}
                >
                  <Typography variant="h3" noWrap>
                    {row.assessmentName ?? '-'}
                  </Typography>
                </TableCell>

                <TableCell sx={{ textAlign: 'center', ...cellSx }}>
                  <Typography variant="h3" noWrap>
                    {(row.assessmentType &&
                      getAssessmentTypeLabel(row.assessmentType)) ??
                      '-'}
                  </Typography>
                </TableCell>

                <TableCell sx={{ textAlign: 'center', ...cellSx }}>
                  <Typography variant="h3" noWrap>
                    {row.subject ?? '-'}
                  </Typography>
                </TableCell>

                <TableCell sx={{ textAlign: 'center', ...cellSx }}>
                  <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                    <Chip
                      label="View Report"
                      color="primary"
                      size="small"
                      clickable
                      onClick={() => row && generateReports?.(row)}
                      sx={{ cursor: 'pointer' }}
                    />
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </TableContainer>
  );
};
